// Copyright © 2024 Broadcom Inc. and its subsidiaries. All Rights Reserved.
import React, { useEffect, useState } from 'react';
import { injectIntl } from 'react-intl';
import forEach from 'lodash/forEach';
import keys from 'lodash/keys';
import each from 'lodash/each';
import isArray from 'lodash/isArray';
import cloneDeep from 'lodash/cloneDeep';
import find from 'lodash/find';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { Button, Tooltip } from '@material-ui/core';
import HelpIcon from '@material-ui/icons/Help';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';

import { getI18nFormattedMessage } from '../../../utils/intl';
import FormSelect from '../../FormSelect';
import {
  FILTER_TYPE_RANK,
  FILTER_TYPE_NAME,
  FILTER_TYPE_RANGE,
  FILTER_TYPE_CODE,
  FILTER_TYPE_URI,
} from '../../../constants/filter';
import {
  MetricFilterDropdownValues,
  FilterList, RespCodeList,
} from '../../../constants/customReport';
import messages from '../../../labels/messages_en';

import FilterLabel from '../AddFilterPanelFilterLabel';
import CustomFilter from '../CustomFilter';
import ProgressBar from '../ProgressBar';
import CustomFilterButtonBar from '../CustomFilterButtonBar';

// import withTheme from '../../../core/jss';
import { withStyles } from '@material-ui/core';
import Theme from './styles';

const MAX_ALLOWED_SLOTS = 10;

const dimensionMap = { apiId: 'api', appId: 'app', orgId: 'org', gatewayServerId: 'gatewayServer', uri: 'uri', respCode: 'respCode', apiKeyId: 'apiKey' };

const AddPanelModal = (props) => {
  const [state, updateState] = useState({
    selectedMetricType: MetricFilterDropdownValues.value[0].uuid,
    selectedDimension: 0,
    popUpFilterEntityType: '',
    filterFieldsData: [],
    filterGroupByData: [],
    dimensionOrder: [],
  });
  const setState = (value) => {
    if (typeof value === 'function') {
      updateState(currentValue => ({ ...currentValue, ...value(state) }));
    } else {
      updateState(currentValue => ({ ...currentValue, ...value }));
    }
  }
  const [selectedDataBackup, setSelectedDataBackup] = useState({});
  const [selectedMetricBackup, setSelectedMetricBackup] = useState({});
  const [baseState, setBaseState] = useState({});
  const [isInvalidInput, setIsInvalidInput] = useState(false);
  const [isFilterGrouped, setIsFilterGrouped] = useState(false);
  const [navigateToFilterPanel, setNavigateToFilterPanel] = useState(false);
  const [dimensionToNavigate, setDimensionToNavigate] = useState(-1);
  const [appliedFiltersGroupBys, setAppliedFiltersGroupBys] = useState({});

  useEffect(() => {
    if (navigateToFilterPanel && (dimensionToNavigate !== -1)) {
      showFilterPanel(dimensionToNavigate);
      setNavigateToFilterPanel(false);
      if(dimensionToNavigate > 0)
      {
        props.onEntityTypeChange(
          FilterList[dimensionToNavigate - 1] && FilterList[dimensionToNavigate - 1].value);
      }
    }
  }, [navigateToFilterPanel]);

  useEffect(() => {
    const derivedDimensionOrder = [];
    if (props.selectedPanelDataSource) {
      props.selectedPanelDataSource.forEach(
        data => {
          let filterValue = '';
          if (data.dimensionId !== undefined) {
            filterValue = FilterList[data.dimensionId - 1].value;
            derivedDimensionOrder.push(filterValue);
          }
        });
    }
    const filterGroupByData = [];
    const filterFieldsData = [];
    setSelectedDataBackup(cloneDeep(props.selectedPanelDataSource));
    setSelectedMetricBackup(cloneDeep(props.selectedMetric));
    forEach(props.selectedPanelDataSource, dataSource => {
      if (dataSource.type === 'top' || dataSource.type === 'range') {
        filterGroupByData.push(dataSource);
      } else if (
        dataSource.type === 'in' ||
        dataSource.dimension === 'uri' ||
        dataSource.dimension === 'respCode') {
        filterFieldsData.push(dataSource);
      }
    });
    const derivedSelectedDimension = props.selectedDimension || 0;
    const derivedFilterFieldsData = filterFieldsData || [];
    const updatedState = {
      selectedDimension: derivedSelectedDimension,
      dimensionOrder: derivedDimensionOrder || [],
      filterGroupByData: filterGroupByData || [],
      filterFieldsData: derivedFilterFieldsData,
      selectedMetricType: props.selectedMetric
        ? props.selectedMetric.uuid
        : MetricFilterDropdownValues.value[0].uuid,
    };
    setState(updatedState);
    setBaseState(cloneDeep({
      ...state,
      updatedState,
    }));
    groupFilter(derivedFilterFieldsData, derivedSelectedDimension);
    setAppliedFiltersGroupBys(getGroupBys(filterGroupByData, filterFieldsData));
    if (props.selectedDimension !== undefined) {
      setDimensionToNavigate(props.selectedDimension);
      setNavigateToFilterPanel(true);
    }
  }, []);
  const getData = (selectedMetricUuid, selectedFilters, isChartLoadRequired) => {
    const selectedMetric =
      find(MetricFilterDropdownValues.value, item => item.uuid === selectedMetricUuid);
    props.createChart(
      selectedMetric,
      props.editPanelId,
      selectedFilters,
      state.dimensionOrder,
      isChartLoadRequired,
    );
  };
  const getTotalCount = (filterGroupByData, filterFieldsData) => {
    let count = 1;
    if (filterGroupByData.length) {
      filterGroupByData.forEach(item => {
        if (item.type === 'top') {
          count *= item.limit;
        } else if (item.type === 'range') {
          count *= item.ranges.length;
        }
      });
    }
    if (filterFieldsData.length) {
      filterFieldsData.forEach(item => {
        if (!item.isGrouped && item.type !== 'default') {
          if (item.type === 'or') {
            count *= item.fields.length;
          } else if (isArray(item.values)) {
            count *= item.values.length;
          } else {
            count *= keys(item.values).length;
          }
        }
      });
    }
    return count;
  };
  const getGroupBys = (filterGroupByData, filterFieldsData) => {
    const groupBysObj = {};
    filterGroupByData
      .filter(entry => entry.type === 'top' || entry.type === 'range')
      .forEach(entry => {
        groupBysObj[entry.dimension] = entry.type;
      });
    filterFieldsData
      .filter(entry => !entry.isGrouped && entry.type !== 'default')
      .forEach(entry => {
        groupBysObj[entry.dimension] = entry.type;
      });
    return groupBysObj;
  };
  const getRespCodeRangesForFilter = (filter) => {
    const selectedItems = {};
    if (filter.fields) {
      forEach(filter.fields, (range) => {
        selectedItems[`${range.lower}-${range.upper}`] = true;
      });
    } else {
      selectedItems[`${filter.lower}-${filter.upper}`] = true;
    }
    return selectedItems;
  };
  const getRespCodeRangesForGroupBy = (filter) => {
    const selectedItems = {};
    if (filter.ranges) {
      forEach(filter.ranges, (range) => {
        selectedItems[`${range.from}-${range.to}`] = true;
      });
    }
    return selectedItems;
  };
  const getRespCodeRanges = (filter) => {
    if (filter.type === 'range') {
      return getRespCodeRangesForGroupBy(filter);
    }
    return getRespCodeRangesForFilter(filter);
  };
  const getFilterFieldsData = (isGrouped) => {
    const { filterFieldsData, selectedDimension } = state;
    let { filterGroupByData } = state;
    if (isGrouped) {
      const fields = [];
      const dimensionSelected = FilterList[selectedDimension - 1];
      const groupByEntryList = filterGroupByData.filter(field =>
        field.dimensionId === selectedDimension);
      filterGroupByData = filterGroupByData.filter(field =>
        field.dimensionId !== selectedDimension);
      const groupByEntry = groupByEntryList.length && groupByEntryList[0];
      each(groupByEntry.ranges, (range) => {
        fields.push({
          type: 'bound',
          dimension: dimensionSelected.value,
          dimensionId: dimensionSelected.id,
          lower: range.from,
          upper: range.to,
        });
      });
      if (fields.length) {
        filterFieldsData.push({
          type: 'or',
          fields,
          dimension: dimensionSelected.value,
          dimensionId: dimensionSelected.id,
          isGrouped,
        });
      }
    }
    return { filterFieldsData, filterGroupByData };
  }
  const getFilterGroupByData = (isGrouped) => {
    const { filterGroupByData, selectedDimension } = state;
    let { filterFieldsData } = state;
    if (!isGrouped) {
      const ranges = [];
      const dimensionSelected = FilterList[selectedDimension - 1];
      const filterEntryList = filterFieldsData.filter(field =>
        field.dimensionId === selectedDimension);
      filterFieldsData = filterFieldsData.filter(field =>
        field.dimensionId !== selectedDimension);
      const filterEntry = filterEntryList.length && filterEntryList[0];
      each(filterEntry.fields, (range) => {
        const key = `${range.lower}-${range.upper}`;
        const respCode = RespCodeList[key] || {};
        const { labelKey, value } = respCode;
        ranges.push({
          from: value[0],
          to: value[1],
          name: labelKey && props.intl.formatMessage(getMessage(labelKey)),
        });
      });
      if (ranges.length) {
        filterGroupByData.push({
          type: 'range',
          ranges,
          dimension: dimensionSelected.value,
          dimensionId: dimensionSelected.id,
          isGrouped,
        });
      }
    }
    return { filterFieldsData, filterGroupByData };
  }
  // entityType could be 0 when no filter is selected
  const isDisableGrouped = (filterType, entityType) => {
    const dimensionExists = keys(appliedFiltersGroupBys).find(
      entry => dimensionMap[entry] === entityType,
    );
    return filterType !== FILTER_TYPE_RANK &&
    keys(appliedFiltersGroupBys).length >= 3 && !dimensionExists;
  }
  const groupFilter = (filterFieldsDataParam, selectedDimensionParam) => {
    let isGroupedVar = false;
    const selectedFilterObj = filterFieldsDataParam.find(
      data => data.dimensionId === selectedDimensionParam,
    );
    if (selectedFilterObj !== undefined && keys(selectedFilterObj).length) {
      isGroupedVar = selectedFilterObj.isGrouped !== undefined ?
        selectedFilterObj.isGrouped : false;
    } else {
      isGroupedVar = !(selectedFilterObj === undefined);
    }
    setIsFilterGrouped(isGroupedVar);
  }
  const cancelSelection = () => {
    if (selectedDataBackup) {
      getData(selectedMetricBackup.uuid, selectedDataBackup, false);
    } else {
      props.closeModal();
    }
  };

  const getPopupFilterData = (id) => {
    let filterDataSource = {};
    switch (id) {
      case 1:
        filterDataSource = props.apiList;
        break;
      case 2:
        filterDataSource = props.appList;
        break;
      case 3:
        filterDataSource = props.gatewayList;
        break;
      case 4:
        filterDataSource = props.orgList;
        break;
      case 5:
        filterDataSource = RespCodeList;
        break;
      case 7:
        filterDataSource = props.apiKeyDetailsMap;
        break;
      default:
    }
    return filterDataSource;
  }
  
  const popupFilterData = getPopupFilterData(dimensionToNavigate);
  const showFilterPanel = id => {
    setBaseState(cloneDeep(state));

    const derivedDimensionOrder = [...state.dimensionOrder];
    const filterValue = FilterList[id - 1] && FilterList[id - 1].value;
    const filterDimension = FilterList[id - 1] && FilterList[id - 1].filterDimension;
    let calculatedSelectedFilterData = {};
    const derivedFilterGroupByData = cloneDeep(state.filterGroupByData);
    if (filterValue && !derivedDimensionOrder.includes(filterValue)) {
      derivedDimensionOrder.push(FilterList[id - 1].value);
      calculatedSelectedFilterData = {
        filterType: FILTER_TYPE_RANK,
        rank: 1,
        selectedItems: {},
      };
      derivedFilterGroupByData.push({
        dimension: filterDimension,
        type: 'top',
        dimensionId: id,
        limit: 1,
      });
    } else {
      const groupByEntry = state.filterGroupByData.find(
        entity => entity.dimension === filterDimension && entity.type !== 'default',
      );
      if (groupByEntry) {
        if (groupByEntry.type === 'range') {
          calculatedSelectedFilterData = {
            filterType: FILTER_TYPE_RANGE,
            selectedItems: getRespCodeRanges(groupByEntry),
          };
        } else {
          calculatedSelectedFilterData = {
            filterType: FILTER_TYPE_RANK,
            rank: groupByEntry.limit,
            selectedItems: {},
          };
        }
      } else {
        const filterFieldsEntry = state.filterFieldsData.find(
          entity => entity.dimension === filterDimension,
        );
        if (filterFieldsEntry) {
          const { type, dimension } = filterFieldsEntry;
          if (dimension === 'uri') {
            calculatedSelectedFilterData = {
              filterType: FILTER_TYPE_URI,
              selectedItems: filterFieldsEntry.values,
            };
          } else if (dimension === 'respCode' && type === 'or') {
            calculatedSelectedFilterData = {
              filterType: FILTER_TYPE_RANGE,
              selectedItems: getRespCodeRanges(filterFieldsEntry),
            };
          } else if (dimension === 'respCode' && type === 'in') {
            calculatedSelectedFilterData = {
              filterType: FILTER_TYPE_CODE,
              selectedItems: filterFieldsEntry.values,
            };
          } else {
            calculatedSelectedFilterData = {
              filterType: FILTER_TYPE_NAME,
              selectedItems: filterFieldsEntry.values,
            };
          }
        }
      }
    }

    groupFilter(state.filterFieldsData, id);
    let derivedDisableGrouped = false;
    if (calculatedSelectedFilterData) {
      derivedDisableGrouped =
        isDisableGrouped(calculatedSelectedFilterData.filterType, filterValue);
      setIsFilterGrouped(prevValue => prevValue||derivedDisableGrouped);
    }
    setState({
      disableGrouped: derivedDisableGrouped,
      filterGroupByData: derivedFilterGroupByData,
      selectedDimension: id,
      popUpFilterEntityType: filterValue,
      selectedFilterType: calculatedSelectedFilterData && calculatedSelectedFilterData.filterType,
      selectedFilterData: calculatedSelectedFilterData,
      dimensionOrder: derivedDimensionOrder,
    });
  };
  const removeFilterPopUp = () => {
    setState({
      ...baseState,
      selectedDimension: 0,
    });
  };
  const applyFilter = (isGrouped) => {
    const { selectedFilterType, selectedDimension } = state;
    let filterFieldsData = cloneDeep(state.filterFieldsData);
    let filterGroupByData = cloneDeep(state.filterGroupByData);
    if (selectedFilterType === FILTER_TYPE_NAME ||
      (selectedFilterType === FILTER_TYPE_RANGE && isFilterGrouped) ||
      selectedFilterType === FILTER_TYPE_CODE ||
      selectedFilterType === FILTER_TYPE_URI) {
      filterFieldsData = state.filterFieldsData.map(data => (
        data.dimensionId === selectedDimension ?
          { ...data, isGrouped } : data));
    }
    const filterFieldsDimensions = [];
    filterFieldsData.forEach(data => {
      if (data.type === 'in' && !data.isGrouped) {
        filterFieldsDimensions.push(data.dimension);
      }
    });
    const defaultGroupByData = [];
    filterFieldsDimensions.forEach(field => {
      const existingDimension = filterGroupByData.find(
        data => data.type === 'default' && data.dimension === field);
      if (!existingDimension) {
        defaultGroupByData.push({ type: 'default', dimension: field });
      }
    });
    filterGroupByData = [...filterGroupByData, ...defaultGroupByData];
    const updatedState = {
      selectedDimension: 0,
      filterGroupByData,
      filterFieldsData,
    };
    setState(updatedState);
    setBaseState({ ...cloneDeep(updatedState) });
    setAppliedFiltersGroupBys(getGroupBys(filterGroupByData, filterFieldsData));
  };
  const updatedGroupSelection = (isGrouped, selectedFilterTypeParam) => {
    setIsFilterGrouped(isGrouped);
    let filterFieldsData = [];
    let filterGroupByData = [];
    if (selectedFilterTypeParam === FILTER_TYPE_RANGE) {
      let field = {};
      if (isGrouped) {
        field = getFilterFieldsData(isGrouped);
        filterFieldsData = field.filterFieldsData;
        filterGroupByData = field.filterGroupByData;
      } else {
        field = getFilterGroupByData(isGrouped);
        filterFieldsData = field.filterFieldsData;
        filterGroupByData = field.filterGroupByData;
      }
    } else {
      filterFieldsData = state.filterFieldsData.map(data => (
        data.dimensionId === state.selectedDimension ?
          { ...data, isGrouped } : data));
      filterGroupByData = state.filterGroupByData;
    }
    setState({
      filterFieldsData,
      filterGroupByData,
    });
  };
  const populateData = (filterType, isNotValid, count, selectedData) => {
    setIsInvalidInput(isNotValid);
    const dimensionSelected = FilterList[state.selectedDimension - 1];
    let filterGroupByData = cloneDeep(state.filterGroupByData);
    let filterFieldsData = cloneDeep(state.filterFieldsData);
    switch (filterType) {
      case FILTER_TYPE_RANK: {
        if (isNotValid) {
          filterGroupByData = filterGroupByData.filter(
            item => item.dimension !== dimensionSelected.filterDimension,
          );
        } else {
          filterGroupByData = filterGroupByData.filter(
            item => !(item.dimension === dimensionSelected.filterDimension
          && item.type !== 'top'),
          );
          const itemToChange = filterGroupByData.find(
            item => item.dimension === dimensionSelected.filterDimension &&
            item.type !== 'default',
          );
          if (itemToChange) {
            itemToChange.limit = count;
          } else {
            filterGroupByData.push({
              dimension: dimensionSelected.filterDimension,
              dimensionId: dimensionSelected.id,
              type: 'top',
              limit: count,
            });
          }
          filterFieldsData = state.filterFieldsData.filter(
            entry => entry.dimension !== dimensionSelected.filterDimension,
          );
        }
        break;
      }
      case FILTER_TYPE_CODE:
      case FILTER_TYPE_NAME: {
        if (keys(selectedData).length === 0) {
          filterFieldsData = filterFieldsData.filter(
            item => item.dimension !== dimensionSelected.filterDimension,
          );
        } else {
          const itemToChange = filterFieldsData.find(
            item => item.dimension === dimensionSelected.filterDimension,
          );
          if (itemToChange) {
            itemToChange.values = selectedData;
          } else {
            filterFieldsData.push({
              dimension: dimensionSelected.filterDimension,
              dimensionId: dimensionSelected.id,
              type: 'in',
              values: selectedData,
              isGrouped: isFilterGrouped,
            });
          }
        }
        filterGroupByData = state.filterGroupByData.filter(
          entry =>
            entry.dimension !== dimensionSelected.filterDimension ||
            (entry.type !== 'top' && entry.type !== 'range'),
        );
        break;
      }
      case FILTER_TYPE_URI: {
        filterGroupByData = state.filterGroupByData.filter(
          entry =>
            entry.dimension !== dimensionSelected.value || entry.type !== 'top',
        );
        let notEmptyValues = [];
        if (selectedData.length) {
          notEmptyValues = selectedData.filter(item => item.name !== '');
        }
        if (!notEmptyValues.length) {
          filterFieldsData =
            filterFieldsData.filter(item => item.dimension !== dimensionSelected.value);
        } else {
          const itemToChange = filterFieldsData.find(
            item => item.dimension === dimensionSelected.value && item.type === 'regex',
          );
          if (itemToChange) {
            itemToChange.values = notEmptyValues;
          } else if (notEmptyValues.length) {
            filterFieldsData.push({
              dimension: dimensionSelected.value,
              dimensionId: dimensionSelected.id,
              type: 'regex',
              values: notEmptyValues,
              isGrouped: isFilterGrouped,
            });
          }
        }
        break;
      }
      case FILTER_TYPE_RANGE: {
        const fields = [];
        const ranges = [];
        each(keys(selectedData), (key) => {
          const respCode = RespCodeList[key] || {};
          const { labelKey, value } = respCode;
          if (value && value.length) {
            if (!isFilterGrouped) {
              ranges.push({
                from: value[0],
                to: value[1],
                name: labelKey && props.intl.formatMessage(getMessage(labelKey)),
              });
            } else {
              fields.push({
                type: 'bound',
                dimension: dimensionSelected.value,
                dimensionId: dimensionSelected.id,
                lower: value[0],
                upper: value[1],
              });
            }
          }
        });
        filterFieldsData = filterFieldsData.filter(
          item => item.dimension !== dimensionSelected.filterDimension,
        );
        if (!!isFilterGrouped && fields.length > 0) {
          filterFieldsData.push({
            type: 'or',
            fields,
            dimension: dimensionSelected.filterDimension,
            dimensionId: dimensionSelected.id,
            isGrouped: isFilterGrouped,
          });
        }

        filterGroupByData = state.filterGroupByData.filter(
          entry =>
            entry.dimension !== dimensionSelected.value,
        );
        if (!isFilterGrouped && ranges.length > 0) {
          filterGroupByData.push({
            type: 'range',
            ranges,
            dimension: dimensionSelected.filterDimension,
            dimensionId: dimensionSelected.id,
            isGrouped: isFilterGrouped,
          });
        }
        break;
      }
      default:
    }
    const additionalStateUpdate = {};
    if (state.selectedFilterType !== filterType) {
      additionalStateUpdate.selectedFilterType = filterType;
      const derivedDisableGrouped = isDisableGrouped(filterType, state.popUpFilterEntityType);
      const updatedIsFilterGrouped = derivedDisableGrouped ? true : isFilterGrouped;
      setIsFilterGrouped(updatedIsFilterGrouped);
      additionalStateUpdate.disableGrouped = derivedDisableGrouped;
    }
    setState({
      filterGroupByData,
      filterFieldsData,
      ...additionalStateUpdate,
    });
  };
  const handleFilterLabelClick = dimensionId => {
    setState({
      ...baseState,
      selectedDimension: 0,
    });
    setDimensionToNavigate(dimensionId);
    setNavigateToFilterPanel(true);
  };
  const changeMetricSelection = evt => {
    setState({
      selectedMetricType: evt.currentTarget.value,
    });
  };
  const removeFilter = () => {
    setIsInvalidInput(false);
    const updatedDimensionOrder = state.dimensionOrder.length &&
      state.dimensionOrder.filter(
        data => data !== FilterList[state.selectedDimension - 1].value,
      );
    let newState = {};
    if (state.selectedFilterType === FILTER_TYPE_NAME ||
      state.selectedFilterType === FILTER_TYPE_CODE ||
      state.selectedFilterType === FILTER_TYPE_URI ||
      (state.selectedFilterType === FILTER_TYPE_RANGE &&
        isFilterGrouped)) {
      const updatedFilterData = state.filterFieldsData.filter(
        data => data.dimensionId !== state.selectedDimension,
      );
      newState = {
        filterFieldsData: updatedFilterData,
        selectedDimension: 0,
        selectedFilterData: {
          selectedItems: {},
        },
        dimensionOrder: updatedDimensionOrder,
      };
    } else {
      const updatedGroupByData = state.filterGroupByData.filter(
        data => data.dimensionId !== state.selectedDimension,
      );
      newState = {
        filterGroupByData: updatedGroupByData,
        selectedDimension: 0,
        selectedFilterData: {
          ...state.selectedFilterData,
          rank: 1,
        },
        dimensionOrder: updatedDimensionOrder,
      }
    }
    const updatedState = { ...state, ...newState };
    setAppliedFiltersGroupBys(
      getGroupBys(updatedState.filterGroupByData, updatedState.filterFieldsData),
    );
    setState(updatedState);
    setBaseState(updatedState);
  };
  const renderFilterList = (classes, filter) => (
    <div
      className={classes.filterName}
      data-apim-test={`filter-${filter.filterDimension}`}
      key={filter.id}
      onClick={() => {
        setDimensionToNavigate(filter.id);
        setNavigateToFilterPanel(true);
      }}
    >
      {props.intl.formatMessage(getMessage(filter.text))}
    </div>
  );
  const getMessage = (id) => {
    return { id, description: messages[id], defaultMessage: messages[id] };
  };

  const { classes, closeModal, handleSearchFilterNameChange } = props;
  const selectedFilters = [];
  state.dimensionOrder.map(dimension => {
    let filterData = {};
    filterData =
      state.filterFieldsData &&
      state.filterFieldsData.find(
        fieldData => dimensionMap[fieldData.dimension] === dimension,
      );
    if (!filterData) {
      filterData =
        state.filterGroupByData &&
        state.filterGroupByData.find(
          data => dimensionMap[data.dimension] === dimension &&
          data.type !== 'default',
        );
    }
    if (filterData) {
      selectedFilters.push(filterData);
      if (!filterData.isGrouped) {
        const defaultTypeGroupByData = state.filterGroupByData &&
        state.filterGroupByData.find(
          data => data.type === 'default' &&
            dimensionMap[data.dimension] === dimension,
        );
        if (defaultTypeGroupByData) {
          selectedFilters.push(defaultTypeGroupByData);
        }
      }
    }
  });
  const selectedEntityCount = getTotalCount(state.filterGroupByData, state.filterFieldsData);
  const dimensionExists = keys(appliedFiltersGroupBys).find(
    entry => dimensionMap[entry] === state.popUpFilterEntityType,
  );
  const disableApplyBtn = isInvalidInput || (selectedEntityCount > MAX_ALLOWED_SLOTS)
    || (selectedEntityCount === 0) || (
      (!dimensionExists && state.selectedFilterType === FILTER_TYPE_RANK &&
        keys(appliedFiltersGroupBys).length >= 3)
      );
  const addFilterText = state.selectedDimension > 0 ? FilterList[state.selectedDimension - 1].text
    : 'label.analytics.customReport.dashboard.label.addFilter.text';

  return (
    <div className={classes.panelModal}>
      <div className={classes.panel}>
        <div className={classes.panelHeader}>
          <div className={classes.addMetricContainer}>
            <div className={classes.chartMetricLabel}>
              {props.intl.formatMessage(getMessage('label.analytics.customReport.dashboard.label.chartMetric.text'))}
            </div>
            <div className={classes.addMetricSelectBox}>
              <FormSelect
                data={MetricFilterDropdownValues.value}
                handleChange={changeMetricSelection}
                hideLabel
                id={'metric-select'}
                labelClass={classes.metricSelectLabel}
                name={'select'}
                noNoneOption
                fieldContainerClass={classes.metricSelectFieldContainer}
                selectFieldClass={classes.metricSelectField}
                size={'medium'}
                value={
                  state.selectedMetricType ||
                  MetricFilterDropdownValues.value[0].uuid
                }
              />
            </div>
          </div>
          <div className={classes.addFilterContainer}>
            {state.selectedDimension > 0 ? (
              <KeyboardArrowDownIcon
                className={clsx(classes.icon, classes.backIcon)}
                onClick={() => removeFilterPopUp()}
              />
            ) : null}
            <div className={classes.addFilterLabel}>
              {props.intl.formatMessage(getMessage(addFilterText))}
            </div>
          </div>
        </div>
        <div className={classes.panelBody}>
          <div className={classes.metricView}>
            <FilterLabel
              filterLabels={selectedFilters}
              handleClick={handleFilterLabelClick}
              apiList={props.apiList}
              apiKeyDetailsMap={props.apiKeyDetailsMap}
              appList={props.appList}
              orgList={props.orgList}
              gatewayList={props.gatewayList}
              RespCodeList={RespCodeList}
            />
          </div>
          <div className={classes.filterView} data-apim-test="filter-view" >
            {state.selectedDimension >= 1 ? (
              <CustomFilter
                entityType={state.popUpFilterEntityType}
                filterTypeIndex={state.selectedDimension}
                showFilter
                selectedFilterType={state.selectedFilterType}
                entityNameMap={popupFilterData}
                maxAllowedEntities={MAX_ALLOWED_SLOTS}
                filterInitState={state.selectedFilterData}
                populateData={populateData}
                isGroupedSelected={isFilterGrouped}
                handleSearchFilterNameChange={handleSearchFilterNameChange}
              />
            ) : (
              FilterList.map(filter => {
                if (props.isExternalUser) {
                  if (props.isOrgPublisher || !(filter.id === 3 || filter.id === 4)) {
                    return renderFilterList(classes, filter);
                  }
                  return null;
                }
                return renderFilterList(classes, filter);
              })
            )}
          </div>
        </div>
        <div className={classes.panelFooter}>
          <div className={classes.dataLimitView}>
            <div className={classes.dataLimitContainer}>
              <span>
                {props.intl.formatMessage(getMessage('label.analytics.customReport.dashboard.chartDataLimit.label'))}
              </span>
              <Tooltip
                classes={classes}
                title={<div>
                  <p>
                    {getI18nFormattedMessage('label.analytics.customReport.dashboard.chartData.tooltip1.label')}
                  </p>
                  <em>
                  {getI18nFormattedMessage('label.analytics.customReport.dashboard.chartData.tooltip2.label')}
                  </em>
                </div>}
                arrow
                placement="right"
              >
                <HelpIcon className={classes.helpIcon}/>
              </Tooltip>
              <span
                className={
                  selectedEntityCount > MAX_ALLOWED_SLOTS
                    ? classes.limitValueRed
                    : classes.limitValue
                }
              >
                {selectedEntityCount}/{MAX_ALLOWED_SLOTS}
              </span>
            </div>
            <ProgressBar
              count={selectedEntityCount}
              limit={MAX_ALLOWED_SLOTS}
            />
          </div>
          {state.selectedDimension === 0 ||
          state.selectedDimension === 'All Data' ? (
            <div className={classes.actionButtons}>
              <Button
                className={classes.getDataBtn}
                data-apim-test={'get-data-btn'}
                onClick={() => getData(state.selectedMetricType, selectedFilters, true)}
              >
                {props.intl.formatMessage(getMessage('label.analytics.customReport.dashboard.button.getData.label'))}
              </Button>
              <Button
                className={classes.cancelBtn}
                onClick={cancelSelection}
                data-apim-test={'cancel-btn'}
              >
                {props.intl.formatMessage(getMessage('label.analytics.customReport.dashboard.button.cancel.label'))}
              </Button>
            </div>
          ) : (
            <CustomFilterButtonBar
              onCancel={removeFilterPopUp}
              onApply={applyFilter}
              disableApplyBtn={disableApplyBtn}
              showGroupCheckBox={state.selectedFilterType !== FILTER_TYPE_RANK}
              isGroupedSelected={isFilterGrouped}
              disableGrouped={state.disableGrouped}
              removeFilter={removeFilter}
              updatedGroupSelection={(isGrouped) =>
                updatedGroupSelection(isGrouped, state.selectedFilterType)}
            />
          )}
        </div>
      </div>
      <div
        className={classes.overlay}
        data-apim-test={'overlay'}
        onClick={closeModal}
      />
    </div>
  );
}

AddPanelModal.propTypes = {
  classes: PropTypes.object,
  closeModal: PropTypes.func,
  createChart: PropTypes.func,
  apiList: PropTypes.object,
  apiKeyDetailsMap: PropTypes.object,
  appList: PropTypes.object,
  orgList: PropTypes.object,
  gatewayList: PropTypes.object,
  selectedDimension: PropTypes.number,
  selectedPanelDataSource: PropTypes.array,
  selectedMetric: PropTypes.object,
  editPanelId: PropTypes.string,
  intl: PropTypes.object,
  isExternalUser: PropTypes.bool,
  isOrgPublisher: PropTypes.bool,
  handleSearchFilterNameChange: PropTypes.func,
  onEntityTypeChange: PropTypes.func,
};

export default withStyles(Theme)(injectIntl(AddPanelModal));
